import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, DollarSign, TrendingUp, Clock, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";
import WithdrawDialog from "./WithdrawDialog";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtMoneyPrecise = (n) => `$${Number(n || 0).toFixed(4)}`;

function StatCard({ label, value, icon: Icon, tone = "default", hint }) {
  const tones = {
    default: "text-white",
    success: "text-emerald-300",
    warning: "text-amber-300",
    accent: "text-blue-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={18} className="text-slate-500" />
      </div>
      <div className={`font-display text-3xl tracking-tight ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function ArtistRoyalties() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/artist/royalties");
      setData(data);
    } catch (e) {
      toast.error("Failed to load royalties");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  }

  const empty = !data || data.line_count === 0;

  return (
    <div className="space-y-6" data-testid="artist-royalties">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Royalties</h2>
          <p className="mt-1.5 text-slate-400">
            Track your earnings across every store and territory. Request a payout once your balance is ready.
          </p>
        </div>
        <button
          data-testid="withdraw-btn"
          disabled={!data || data.available <= 0}
          onClick={() => setShowWithdraw(true)}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Wallet size={16} /> Withdraw
        </button>
      </div>

      <WithdrawDialog
        open={showWithdraw}
        onOpenChange={setShowWithdraw}
        available={data?.available || 0}
        onSuccess={load}
      />

      {empty ? (
        <EmptyState
          title="No royalties yet"
          desc="Your earnings will appear here as soon as your admin uploads the next monthly DSR statement."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total earnings" value={fmtMoney(data.total_net)} icon={DollarSign} hint={`${data.line_count} line items`} />
            <StatCard label="Available" value={fmtMoney(data.available)} icon={Wallet} tone="accent" hint="Ready to withdraw" />
            <StatCard label="Pending payouts" value={fmtMoney(data.pending_out)} icon={Clock} tone="warning" hint="Under review" />
            <StatCard label="Paid out" value={fmtMoney(data.paid_out)} icon={TrendingUp} tone="success" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="royalty-periods">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-white font-medium">Statement history</div>
                  <div className="text-xs text-slate-500">Earnings by reporting period</div>
                </div>
              </div>
              {data.by_period.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No statements yet</div>
              ) : (
                <TableWrap>
                  <thead>
                    <tr>
                      <th className={th}>Period</th>
                      <th className={th}>Streams</th>
                      <th className={th}>Gross</th>
                      <th className={th}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_period.map((p) => (
                      <tr key={p.period}>
                        <td className={td + " text-white"}>{p.period}</td>
                        <td className={td}>{Math.round(p.streams).toLocaleString()}</td>
                        <td className={td}>{fmtMoneyPrecise(p.gross)}</td>
                        <td className={td + " text-emerald-300 font-medium"}>{fmtMoneyPrecise(p.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="royalty-retailers">
              <div className="mb-4">
                <div className="text-white font-medium">Top retailers</div>
                <div className="text-xs text-slate-500">By net earnings</div>
              </div>
              {data.by_retailer.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No retailer data</div>
              ) : (
                <ul className="space-y-2.5">
                  {data.by_retailer.slice(0, 10).map((r) => (
                    <li key={r.retailer} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-300 truncate">{r.retailer}</span>
                      <span className="text-emerald-300 font-medium tabular-nums">{fmtMoneyPrecise(r.net)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {data.withdrawals.length > 0 && (
            <div>
              <div className="text-white font-medium mb-3">Withdrawal history</div>
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Amount</th>
                    <th className={th}>PayPal</th>
                    <th className={th}>Status</th>
                    <th className={th}>Reference / Reason</th>
                    <th className={th}>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {data.withdrawals.map((w) => (
                    <tr key={w.request_id} data-testid={`my-withdrawal-${w.request_id}`}>
                      <td className={td + " text-white font-medium"}>{fmtMoney(w.amount)}</td>
                      <td className={td}>{w.paypal_email}</td>
                      <td className={td}><StatusPill status={w.status === "paid" ? "Live" : w.status === "rejected" ? "Rejected" : "Pending"} /></td>
                      <td className={td + " text-xs text-slate-400"}>{w.reference || w.reason || "—"}</td>
                      <td className={td}>{fmtDate(w.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </>
      )}
    </div>
  );
}
